// Phase 2.6 — Partner create/edit modal extracted from Partners.jsx.
// Wraps the tabs (Details + Rate History), the form body, and the modal
// chrome. Parent owns: open/close state, form state + setters, the
// rate-card add/update/remove helpers, the deduction helpers, and the
// save() handler. Pure presentational here.

import { Link } from "react-router-dom";
import { Modal, Btn, Input, Select, MonthPicker } from "@/components/Shared";
import ModalTabs from "@/components/ModalTabs";
import RateHistoryTab from "@/components/RateHistoryTab";
import { Plus, X } from "lucide-react";
import { pctToDec as pct2dec, decToPct as dec2pct } from "@/lib/api";

export default function PartnerFormModal({
  open, onClose,
  editId, form, setForm,
  modalTab, setModalTab,
  rateHistoryKey, setRateHistoryKey,
  gateways,
  onSave,
  // Rate-card + deduction helpers (parent-owned because they need the
  // latest `form` closure and call setForm).
  addCard, updateCard, removeCard,
  addDeduction, updateDeduction, removeDeduction,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editId ? "Edit Partner" : "New Partner"}
      size="lg"
    >
      {editId && (
        <ModalTabs
          testid="partner-modal-tabs"
          active={modalTab}
          onChange={setModalTab}
          tabs={[
            { id: "form", label: "Details", testid: "partner-tab-form" },
            { id: "rate-history", label: "Rate History", testid: "partner-tab-rate-history" },
          ]}
        />
      )}
      {modalTab === "rate-history" && editId ? (
        form.compensation_model === "revenue_share" ? (
          // iter39 — Revenue share history view (no gateway/lob pickers —
          // share % is a single scalar per partner).
          <div className="space-y-3">
            <div className="text-[12px] text-[var(--muted)]">
              Effective-dated history of the revenue share % for {form.partner_name || editId}.
              Each entry was set via the Details tab.
            </div>
            <RateHistoryTab
              scope="partner_revenue_share"
              keyFields={{ partner_id: editId }}
              currentRate={form.revenue_share_pct ?? null}
            />
          </div>
        ) : (
        <div className="space-y-3">
          <div className="text-[12px] text-[var(--muted)]">
            Pick a Gateway × LOB rate card to view its base-rate version timeline for {form.partner_name || editId}.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10.5px] uppercase text-[var(--muted)] font-semibold tracking-wider">Gateway</label>
              <Select
                testid="partner-rh-gateway"
                placeholder="Select gateway"
                options={Array.from(new Set((form.rate_cards || []).map((r) => r.gateway).filter(Boolean)))}
                value={rateHistoryKey.gateway}
                onChange={(v) => setRateHistoryKey({ gateway: v, lob: "" })}
              />
            </div>
            <div>
              <label className="text-[10.5px] uppercase text-[var(--muted)] font-semibold tracking-wider">LOB</label>
              <Select
                testid="partner-rh-lob"
                placeholder={rateHistoryKey.gateway ? "Select LOB" : "Pick gateway first"}
                options={Array.from(new Set((form.rate_cards || []).filter((r) => r.gateway === rateHistoryKey.gateway).map((r) => r.lob).filter(Boolean)))}
                value={rateHistoryKey.lob}
                onChange={(v) => setRateHistoryKey({ ...rateHistoryKey, lob: v })}
                disabled={!rateHistoryKey.gateway}
              />
            </div>
          </div>
          <RateHistoryTab
            scope="partner_lob"
            keyFields={{ partner_id: editId, gateway: rateHistoryKey.gateway, lob: rateHistoryKey.lob }}
            currentRate={(form.rate_cards || []).find((r) => r.gateway === rateHistoryKey.gateway && r.lob === rateHistoryKey.lob)?.partner_base_rate ?? null}
          />
        </div>
        )
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Partner ID</label>
          <Input data-testid="form-partner-id" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })} disabled={!!editId} /></div>
        <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Partner Name <span className="text-[var(--negative)]">*</span></label>
          <Input data-testid="form-partner-name" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} /></div>
        <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Contact</label>
          <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
        <div className="col-span-2">
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Email <span className="text-[10px] text-[var(--muted)] normal-case font-normal">· for recon emails</span></label>
          <Input type="email" data-testid="partner-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="partner@example.com" />
        </div>
        <div className="col-span-2">
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Reimbursement Target</label>
          <Select
            testid="partner-form-reimburse-target"
            options={[
              { value: "Partner", label: "Partner — flow to partner-side reimbursement (default)" },
              { value: "NK", label: "NK — payouts deduct from NK's monthly payable" },
              { value: "RR", label: "RR — payouts deduct from RR's monthly payable" },
            ]}
            value={form.reimbursement_target || "Partner"}
            onChange={(v) => setForm({ ...form, reimbursement_target: v })}
          />
          <div className="text-[10.5px] text-[var(--muted)] mt-1">
            When this partner is paid (via Close Period or Partner Payment History), the auto-derived expense in Monthly Accounting will be tagged to this target. Pick NK or RR if their share comes out of the partner's personal funds.
          </div>
        </div>
        <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

        {/* Compensation model toggle */}
        <div className="col-span-2 border-t border-[var(--line)] pt-3 mt-1">
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Compensation Model <span className="text-[var(--negative)]">*</span></label>
          <div className="flex gap-2 mt-1.5" role="radiogroup" data-testid="compensation-toggle">
            {[
              { v: "fixed_base_rate", l: "Fixed Base Rate", hint: "Partner has a base rate per (Gateway × LOB). Commission = Merchant Rate − Base Rate." },
              { v: "revenue_share", l: "Revenue Share", hint: "Partner gets X% of revenue across their merchants." },
              { v: "fixed_fee", l: "Fixed Fee", hint: "Partner gets a flat monthly fee, not tied to merchants/volume. Payouts tracked in Partner Payment History." },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setForm({ ...form, compensation_model: opt.v })}
                data-testid={`compensation-${opt.v}`}
                className={`flex-1 px-3 py-2.5 text-[12.5px] border rounded-sm text-left transition-colors ${form.compensation_model === opt.v ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line-2)] bg-[var(--surface)] hover:border-[var(--ink-2)]"}`}
              >
                <div className="font-semibold">{opt.l}</div>
                <div className={`text-[10.5px] mt-0.5 ${form.compensation_model === opt.v ? "text-white/60" : "text-[var(--muted)]"}`}>{opt.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {form.compensation_model === "revenue_share" ? (
          <div className="col-span-2">
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Revenue Share (%) <span className="text-[var(--negative)]">*</span></label>
            <div className="relative">
              <Input
                type="number" step="0.01" data-testid="form-rs-pct"
                value={dec2pct(form.revenue_share_pct)}
                onChange={(e) => setForm({ ...form, revenue_share_pct: pct2dec(e.target.value) })}
                placeholder="e.g. 35 for 35%" className="pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] text-sm">%</span>
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-1">Applied to gross commission of every merchant tagged to this partner.</div>
          </div>
        ) : form.compensation_model === "fixed_fee" ? (
          <div className="col-span-2 border-t border-[var(--line)] pt-3 space-y-2">
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Fixed monthly fee <span className="text-[var(--negative)]">*</span></label>
            <div className="grid grid-cols-[1fr,150px,150px] gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Amount (₹)</div>
                <Input type="number" step="1" value={form.fixed_monthly_fee} onChange={(e) => setForm({ ...form, fixed_monthly_fee: e.target.value })} placeholder="50000" data-testid="form-fixed-fee" className="text-right num" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Start month</div>
                <MonthPicker value={form.fixed_fee_start_month} onChange={(v) => setForm({ ...form, fixed_fee_start_month: v })} testid="form-fixed-start" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">End (optional)</div>
                <MonthPicker value={form.fixed_fee_end_month} onChange={(v) => setForm({ ...form, fixed_fee_end_month: v })} testid="form-fixed-end" />
              </div>
            </div>
            <div className="text-[11px] text-[var(--muted)]">This fee auto-creates a "Partner Fixed Fee · {form.partner_name || "…"}" expense in Monthly Accounting every month from the start month (until end, if set). Independent of any merchants.</div>
          </div>
        ) : (
          <div className="col-span-2 border-t border-[var(--line)] pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Partner base rates per Gateway × LOB (%) <span className="text-[var(--negative)]">*</span></label>
              <Btn variant="ghost" testid="add-rate-card" onClick={addCard}><Plus size={12} /> Add row</Btn>
            </div>
            <div className="space-y-2 max-h-60 overflow-auto">
              {(form.rate_cards || []).map((c, i) => {
                const rowGw = gateways.find((g) => g.gateway_name === c.gateway);
                const rowLobOptions = Array.from(
                  new Set((rowGw?.lob_rates || []).map((r) => r.lob).filter(Boolean))
                ).sort();
                // Combined gateway+lob update — calling updateCard twice
                // back-to-back loses the gateway change because both reads
                // close over the same stale `form`. Do both writes via a
                // composite callback so setForm sees one merged update.
                const setRowGateway = (newGw) => {
                  const next = [...(form.rate_cards || [])];
                  next[i] = { ...next[i], gateway: newGw, lob: "" };
                  setForm({ ...form, rate_cards: next });
                };
                return (
                  <div key={`rc-${c.gateway || "_"}-${c.lob || "_"}-${i}`} className="space-y-1">
                    <div className="grid grid-cols-[1fr,1fr,140px,32px] gap-2 items-center">
                      <Select options={gateways.map((g) => g.gateway_name)} value={c.gateway} onChange={setRowGateway} testid={`rc-gw-${i}`} placeholder="Gateway" />
                      <Select
                        options={rowLobOptions}
                        value={c.lob}
                        onChange={(v) => updateCard(i, "lob", v)}
                        testid={`rc-lob-${i}`}
                        placeholder={c.gateway ? "LOB" : "Pick gateway"}
                        disabled={!c.gateway}
                      />
                      <div className="relative">
                        <Input
                          type="number" step="0.01" placeholder="0.95"
                          value={dec2pct(c.partner_base_rate)}
                          onChange={(e) => updateCard(i, "partner_base_rate", pct2dec(e.target.value))}
                          data-testid={`rc-rate-${i}`} className="pr-7"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] text-xs">%</span>
                      </div>
                      <button type="button" onClick={() => removeCard(i)} className="text-[var(--muted)] hover:text-[var(--negative)] p-1.5"><X size={14} /></button>
                    </div>
                    {c.gateway && rowLobOptions.length === 0 && (
                      <div className="text-[10.5px] text-amber-700 pl-1" data-testid={`rc-no-lobs-${i}`}>
                        <b>{c.gateway}</b> has no LOBs configured.{" "}
                        <Link to="/gateways" className="underline hover:text-amber-900">
                          Go to Payment Gateways → add one
                        </Link>
                        , then come back.
                      </div>
                    )}
                  </div>
                );
              })}
              {!form.rate_cards?.length && <div className="text-[12px] text-[var(--muted)]">Add the partner's base rate per (Gateway, LOB). Type a percent — e.g. 0.95 for 0.95% of volume. <b className="text-[var(--negative)]">At least one row is required.</b></div>}
            </div>
          </div>
        )}

        <div className="col-span-2 border-t border-[var(--line)] pt-3 mt-1">
          <div className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider mb-2">Tax setup</div>
        </div>
        <div>
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">PAN</label>
          <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" data-testid="form-pan" />
        </div>
        <div>
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">TDS Rate (%)</label>
          <div className="relative">
            <Input
              type="number" step="0.1"
              value={dec2pct(form.tds_rate)}
              onChange={(e) => setForm({ ...form, tds_rate: pct2dec(e.target.value) })}
              data-testid="form-tds-rate" className="pr-7"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] text-xs">%</span>
          </div>
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <input
            type="checkbox" id="gst_applicable"
            checked={!!form.gst_applicable}
            onChange={(e) => setForm({ ...form, gst_applicable: e.target.checked })}
            data-testid="form-gst-applicable"
            className="h-4 w-4 accent-[var(--accent)]"
          />
          <label htmlFor="gst_applicable" className="text-[13px] text-[var(--ink)] cursor-pointer">Partner is GST-registered (we pay commission + GST)</label>
        </div>
        {form.gst_applicable && (
          <>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">GSTIN <span className="text-[var(--negative)]">*</span></label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27ABCDE1234F1Z5" data-testid="form-gstin" />
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">GST Rate (%) <span className="text-[var(--negative)]">*</span></label>
              <div className="relative">
                <Input
                  type="number" step="0.1"
                  value={dec2pct(form.gst_rate)}
                  onChange={(e) => setForm({ ...form, gst_rate: pct2dec(e.target.value) })}
                  data-testid="form-gst-rate" className="pr-7"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] text-xs">%</span>
              </div>
            </div>
          </>
        )}

        <div className="col-span-2 border-t border-[var(--line)] pt-3 mt-1">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Recurring monthly deductions</div>
              <div className="text-[11px] text-[var(--muted)] mt-0.5">Fixed amounts auto-deducted from this partner's Net Payable every month in the active window. You can override the amount for a specific period later from the Payouts → Adjustments view.</div>
            </div>
            <Btn variant="ghost" testid="add-deduction" onClick={addDeduction}><Plus size={12} /> Add deduction</Btn>
          </div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {(form.standing_deductions || []).map((d, i) => (
              <div key={d.id || i} className="bg-[var(--surface-2)] border border-[var(--line)] p-3 rounded-sm" data-testid={`sd-row-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--muted)] font-semibold">Recurring deduction #{i + 1}</div>
                  <button type="button" onClick={() => removeDeduction(i)} className="text-[var(--muted)] hover:text-[var(--negative)] text-[11px] flex items-center gap-1 uppercase tracking-wider font-semibold" data-testid={`sd-remove-${i}`}><X size={12} /> Remove</button>
                </div>
                <div className="grid grid-cols-[1fr,150px] gap-2 mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Label</div>
                    <Input placeholder="e.g. Monthly retainer" value={d.label} onChange={(e) => updateDeduction(i, "label", e.target.value)} data-testid={`sd-label-${i}`} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Amount (₹)</div>
                    <Input type="number" value={d.amount} onChange={(e) => updateDeduction(i, "amount", e.target.value)} data-testid={`sd-amount-${i}`} className="text-right num" placeholder="45000" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Start month</div>
                    <MonthPicker value={d.start_month} onChange={(v) => updateDeduction(i, "start_month", v)} testid={`sd-start-${i}`} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">End month <span className="text-[var(--muted)] normal-case tracking-normal">(optional — leave blank for open-ended)</span></div>
                    <MonthPicker value={d.end_month} onChange={(v) => updateDeduction(i, "end_month", v)} testid={`sd-end-${i}`} />
                  </div>
                </div>
              </div>
            ))}
            {!form.standing_deductions?.length && <div className="text-[12px] text-[var(--muted)] text-center py-4 border border-dashed border-[var(--line-2)] rounded-sm">No recurring deductions yet. Click "Add deduction" to create one.</div>}
          </div>
        </div>
      </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        {modalTab === "form" && <Btn variant="accent" testid="save-partner" onClick={onSave}>Save</Btn>}
      </div>
    </Modal>
  );
}
